import { useMemo } from "react";

interface ErrorWithCode<TCode extends string> { code: TCode; }
type SpecificError<TCode extends string> = Exclude<TCode, CommonErrorCode>;
type FormatError<TCode extends string> = (code: SpecificError<TCode>) => string | null;

/**
 * Funcion que toma un `error` de query/mutation, y aplica los formatos de error que se le pasen, devolviendo un string con el mensaje de error a mostrar.
 * @param formatErrorCustom Funcion que recibe un codigo de error y devuelve un string con el mensaje de error a mostrar, o null si el mensaje no se ha reconocido.
 * @param error Error que se quiere formatear, o null/undefined si no hay error.
 * @returns String con el mensaje de error a mostrar, o null si el error es null/undefined.
 */
export function useErrorMessage<TCode extends string>(formatErrorCustom: FormatError<TCode>, error: ErrorWithCode<TCode> | null | undefined): string | null {
	return useMemo(() => {
		if (error == null) return null;

		if (isInternalError(error)) return formatError("INTERNAL_APPLICATION_ERROR");
		if (isCommonError(error)) return formatError(error.code);


		return formatErrorCustom((error as { code: SpecificError<TCode>; }).code) ?? formatError("INTERNAL_APPLICATION_ERROR");
	}, [error, formatErrorCustom]);
}


function isCommonError(errorCode: unknown): errorCode is ({ code: CommonErrorCode; }) {
	if (errorCode == null) return false;
	if (typeof errorCode != "object") return false;
	if ("code" in errorCode) return CommonCodes.includes(errorCode.code as CommonErrorCode);
	return false;
}
function formatError(errorCode: CommonErrorCode): string {
	return CommonCodesMessages[errorCode];
}
const CommonCodesMessages = {
	INTERNAL_SERVER_ERROR: "Error interno del servidor. Contacta con soporte (INTERNAL_SERVER_ERROR).",
	INTERNAL_APPLICATION_ERROR: "Error de la aplicación. Contacta con soporte (INTERNAL_APPLICATION_ERROR).",
	PATH_NOT_FOUND_ERROR: "Error inesperado en la comunicación con el servidor. Contacta con soporte (PATH_NOT_FOUND_ERROR).",

	//Auth middleware errors
	TOKEN_NOT_PROVIDED: "Error de la aplicación. Contacta con soporte (TOKEN_NOT_PROVIDED).",
	TOKEN_EXPIRED: "Tu sesión ha caducado. Vuelve a iniciar sesión (TOKEN_EXPIRED).",
	TOKEN_INVALID: "Error de la aplicación. Contacta con soporte (TOKEN_INVALID).",
	FORBIDDEN: "No tienes permisos para realizar esta acción. Si crees que es un error, contacta con soporte (FORBIDDEN).",

	// No lo vamos a considerar común; tiene un bloque de información adicional que puede ser útil para el usuario. Lo dejamos para manejarlo de forma específica.
	// REQUEST_VALIDATION_ERROR: "Error de la aplicación. Contacta con soporte.",
} as const;
const CommonCodes = Object.keys(CommonCodesMessages);
type CommonErrorCode = keyof typeof CommonCodesMessages;

//Safety net in case something weird happens.
function isInternalError(error: unknown): boolean {
	//Si es null, no es un error!
	if (error == null) return false;

	//Tendria que ser un objeto
	if (typeof error != "object") return true;

	//Tendria que tener la propiedad code
	if (!("code" in error)) return true;

	//Tendria que ser un string
	if (typeof error.code != "string") return true;

	//error.code es un string, podemos formatearlo sin miedo.
	return false;
}